<template>
  <van-form
    class="question-container"
    ref="form"
    :show-error="false"
    :scroll-to-error="true"
    :submit-on-enter="false"
    @submit.native.prevent
  >
    <template v-if="presentation === 'candidate-mobile'">
      <QuestionFormNode
        v-for="(field, index) in rootFields"
        :key="field.id"
        :field="field"
        :form-line="formLine"
        :data="data"
        :presentation="presentation"
        :question-number="index + 1"
        @register-uploader="registerUploader"
        @unregister-uploader="unregisterUploader"
      />
    </template>
    <template v-else>
    <template v-for="(field, index) in formLine">
      <van-cell
        class="question"
        :class="{
          'tree-question': field.tree,
          'tree-root-question': field.tree && field.tree.isRoot,
          'tree-child-question': field.tree && !field.tree.isRoot
        }"
        title-class="title"
        label-class="label"
        :key="index"
      >
        <template #title>
          <div v-if="field.tree" class="tree-question-header">
            <span class="tree-level-tag">{{ field.tree.depthLabel }}</span>
            <span v-if="field.tree.isRoot" class="tree-root-text">{{ getTreeLabel(field.tree, 'rootText') }}</span>
            <span v-else class="tree-parent-text">
              {{ getTreeLabel(field.tree, 'parentText') }}
              <strong>{{ field.tree.parentTitle }}</strong>
              <span v-if="field.tree.optionLabel" class="tree-option-text">{{ getTreeLabel(field.tree, 'optionText') }} {{ field.tree.optionLabel }}</span>
            </span>
          </div>
          <span class="question-title" :class="{ 'is-required': field.required }" v-html="field.title"></span>
        </template>
        <template #icon>
          {{ `${index + 1}. ` }}
        </template>
        <template #label>
          <uploaderFile
            v-if="field.elType === 'file'"
            class="upload-file"
            ref="upload"
            :field="field"
            :data="data"
          >
          </uploaderFile>
          <vanCustomField
            v-else
            :data="data"
            :field="field"
          >
          </vanCustomField>
        </template>
      </van-cell>
    </template>
    </template>
  </van-form>
</template>

<script>
import _ from 'lodash'
import vanCustomField from './vanCustomField.vue'
import uploaderFile from './uploadFile.vue'
import QuestionFormNode from './questionFormNode.vue'
export default {
  components: {
    vanCustomField,
    uploaderFile,
    QuestionFormNode
  },
  props: {
    formLine: {
      required: true
    },
    data: {
      required: true
    },
    presentation: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      uploaders: []
    }
  },
  computed: {
    rootFields () {
      return this.formLine.filter(field => {
        const tree = field.tree || {}
        return !tree.parentQuestionId || !tree.parentOptionId
      })
    }
  },
  methods: {
    getTreeLabel (tree, key) {
      const i18nKey = tree[`${key}I18nKey`]
      return i18nKey ? this.$t(i18nKey) : tree[key]
    },
    async submit () {
      try {
        await this.$refs.form.validate()
        let uploadValidate = true
        const uploads = this.presentation === 'candidate-mobile' ? this.uploaders : this.$refs.upload
        _.forEach(uploads, upload => {
          const result = upload.validate()
          if (!result) {
            uploadValidate = false
          }
        })
        return uploadValidate && true
      } catch (e) {
        this.errorInfo = e
        this.$nextTick(() => {
          const el = this.$el.querySelector('.van-field__error-message')
          if (el) {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' })
          }
        })
        return false
      }
    },
    registerUploader (uploader) {
      if (uploader && !this.uploaders.includes(uploader)) this.uploaders.push(uploader)
    },
    unregisterUploader (uploader) {
      this.uploaders = this.uploaders.filter(item => item !== uploader)
    }

  }
}
</script>

<style lang="scss" scoped>
.question-container.van-form {
  .van-cell {
    line-height: 20px;
    font-size: 14px;
  }

  .question {
    position: relative;
    padding: 20px 0;
    border-bottom: 1px solid #DCDFE6;

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    &.tree-question {
      margin: 14px 0 0 0;
      padding: 16px 12px;
      border: 1px solid #D8EAF6;
      border-radius: 14px;
      background: linear-gradient(180deg, #fff 0%, #f8fcff 100%);
      box-shadow: 0 6px 18px rgba(41, 90, 130, .08);
      overflow: visible;

      &:first-child {
        padding-top: 16px;
      }

      &:last-child {
        padding-bottom: 16px;
        border-bottom: 1px solid #D8EAF6;
      }
    }

  }

  .label {
    margin-top: 8px;

    .upload-file {
      margin-top: 12px;
    }
  }

  .title {
    margin-left: 2px;

    .tree-question-header {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 8px;
      color: #63788f;
      font-size: 12px;
      line-height: 18px;
    }

    .tree-level-tag {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 30px;
      height: 20px;
      padding: 0 8px;
      border-radius: 999px;
      color: #fff;
      background: linear-gradient(135deg, #0096cc, #37c0df);
      font-weight: 700;
      line-height: 20px;
    }

    .tree-root-text {
      color: #0d7fa7;
      font-weight: 600;
    }

    .tree-parent-text {
      display: inline-flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 4px;
    }

    .tree-option-text {
      display: inline-flex;
      align-items: center;
      min-height: 20px;
      padding: 0 8px;
      border-radius: 999px;
      color: #0d7fa7;
      background: #e9f7fc;
    }

    .question-title {
      white-space: break-spaces;
      word-break: break-word;
      color: #24384d;
      font-weight: 600;

      &.is-required::after {
        position: relative;
        left: 2px;
        color: #ea7f7f;
        font-size: 14px;
        content: '*';
      }
    }

  }

  ::v-deep {
    .common-field {
      border-bottom: none;

      .van-field__body {
        padding: 5px;
        border: 1px solid #DCDFE6;
        border-radius: 4px;
        min-height: initial;
      }
    }
  }
}
</style>
